'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
  CalendarDays,
  ChevronDown,
  Loader2,
  MapPin,
  Plus,
} from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { EventCardListItem } from '@/components/events/event-card-list-item'
import { EventsPageSearchButton } from '@/app/(marketing)/events/events-page-search-button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { useLoginState } from '@/hooks/use-login-state'
import type {
  CategoryFilterGroupOption,
  EventSearchResponse,
  MetroAreaOption,
} from '@/app/(marketing)/events/types'
import type { IntranetRole, LoginRole } from '@/lib/intranet-preview-shared'

type Prediction = {
  placeId: string
  description: string
}

type GooglePlaceDetails = {
  formattedAddress?: string
  lat?: number | null
  lng?: number | null
  error?: string
}

const LOCATION_RADIUS_OPTIONS = ['25', '50', '75', '100'] as const
const DEFAULT_LOCATION_RADIUS = '50'
const EVENT_STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'HeadCount is Coming', value: 'participating' },
] as const

function canSeeAdvancedEventItems(roles: LoginRole[], intranetRoles: IntranetRole[]): boolean {
  if (roles.includes('admin')) return true
  return intranetRoles.includes('team_leader') || intranetRoles.includes('regional_coordinator')
}

type EventsPageClientProps = {
  eventCategories: CategoryFilterGroupOption[]
  talentCategories: CategoryFilterGroupOption[]
  metroAreas: MetroAreaOption[]
  basePath?: string
  showAdvancedFeatures?: boolean
}

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timeout)
  }, [value, delay])

  return debounced
}

function buildSearchParams(
  current: URLSearchParams,
  updates: Record<string, string | null | undefined>,
): URLSearchParams {
  const next = new URLSearchParams(current)

  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined || value === null || value.trim() === '') {
      next.delete(key)
      continue
    }
    next.set(key, value)
  }

  return next
}

function parseDateParam(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return undefined

  const year = Number(match[1])
  const month = Number(match[2]) - 1
  const day = Number(match[3])
  const date = new Date(year, month, day)

  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    return undefined
  }

  return date
}

function formatDateParam(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDateLabel(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function normalizeRadiusParam(value: string | null): string {
  return LOCATION_RADIUS_OPTIONS.includes(value as (typeof LOCATION_RADIUS_OPTIONS)[number])
    ? value || DEFAULT_LOCATION_RADIUS
    : DEFAULT_LOCATION_RADIUS
}

function EventResultSkeleton() {
  return (
    <Card>
      <CardContent className="p-0">
        <ul className="divide-y theme-card-divider">
          {Array.from({ length: 6 }).map((_, index) => (
            <li key={`event-result-skeleton-${index}`} className="animate-pulse px-4 py-4 md:px-6">
              <div className="mb-2 h-4 w-1/4 rounded bg-muted/60" />
              <div className="mb-2 h-5 w-2/3 rounded bg-muted/70" />
              <div className="h-4 w-1/2 rounded bg-muted/50" />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

export function EventsPageClient({
  eventCategories,
  talentCategories,
  metroAreas,
  basePath = '/events',
  showAdvancedFeatures = false,
}: EventsPageClientProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { roles, intranetRoles } = useLoginState()
  const searchParamsString = searchParams.toString()
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const currentResultKeyRef = useRef(searchParamsString)
  const showAdvancedEventItems =
    showAdvancedFeatures && canSeeAdvancedEventItems(roles, intranetRoles)

  const selectedEventStatus = searchParams.get('eventStatus') || searchParams.get('event-status') || ''
  const selectedCategory = searchParams.get('category') || ''
  const selectedTalentCategory = searchParams.get('talent-category') || searchParams.get('talentCategory') || ''
  const selectedTalentChildCategory =
    searchParams.get('talent-child-category') || searchParams.get('talentChildCategory') || ''
  const selectedDateFrom = searchParams.get('dateFrom') || ''
  const selectedRadius = normalizeRadiusParam(searchParams.get('radius'))
  const selectedMetroAreaId = searchParams.get('metroArea') || ''
  const selectedAddressLabel = searchParams.get('address') || ''
  const hasAddressLocation = Boolean(searchParams.get('lat') && searchParams.get('lng'))

  const [locationQuery, setLocationQuery] = useState('')
  const [metroAreaQuery, setMetroAreaQuery] = useState('')
  const [radiusInput, setRadiusInput] = useState(selectedRadius)
  const [results, setResults] = useState<EventSearchResponse | null>(null)
  const [loadingResults, setLoadingResults] = useState(true)
  const [loadingMoreResults, setLoadingMoreResults] = useState(false)
  const [resultsError, setResultsError] = useState<string | null>(null)
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null)
  const [resultRetrySeed, setResultRetrySeed] = useState(0)

  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [loadingPredictions, setLoadingPredictions] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [typePopoverOpen, setTypePopoverOpen] = useState(false)
  const [locationPopoverOpen, setLocationPopoverOpen] = useState(false)
  const [locationDialogMode, setLocationDialogMode] = useState<'metro' | 'address' | null>(null)
  const [datePopoverOpen, setDatePopoverOpen] = useState(false)

  const selectedCategoryOption = useMemo(() => {
    for (const category of eventCategories) {
      if (category.slug === selectedCategory) {
        return { label: `All ${category.pluralName}` }
      }

      const child = category.children.find((option) => option.slug === selectedCategory)
      if (child) {
        return { label: child.name }
      }
    }

    for (const category of talentCategories) {
      if (category.slug === selectedTalentCategory) {
        return { label: `All ${category.pluralName}` }
      }

      const child = category.children.find((option) => option.slug === selectedTalentChildCategory)
      if (child) {
        return { label: child.name }
      }
    }

    return null
  }, [eventCategories, selectedCategory, selectedTalentCategory, selectedTalentChildCategory, talentCategories])
  const selectedEventStatusOption = EVENT_STATUS_OPTIONS.find(
    (option) => option.value === selectedEventStatus,
  )
  const selectedMetroArea = useMemo(
    () => metroAreas.find((metroArea) => metroArea.id === selectedMetroAreaId) || null,
    [metroAreas, selectedMetroAreaId],
  )
  const selectedDate = useMemo(() => parseDateParam(selectedDateFrom), [selectedDateFrom])

  const selectedFilterLabel = selectedEventStatusOption?.label || selectedCategoryOption?.label || 'All Categories'
  const selectedLocationLabel = selectedAddressLabel || selectedMetroArea?.label || ''
  const debouncedLocationQuery = useDebouncedValue(locationQuery, 300)
  const debouncedRadius = useDebouncedValue(radiusInput, 350)
  const filteredMetroAreas = useMemo(() => {
    const query = metroAreaQuery.trim().toLowerCase()
    if (!query) return metroAreas
    return metroAreas.filter((metroArea) => metroArea.label.toLowerCase().includes(query))
  }, [metroAreaQuery, metroAreas])
  const hasLocation = hasAddressLocation || Boolean(selectedMetroArea)
  const hasMoreResults = Boolean(results && results.page < results.totalPages)

  const buildEventRequestParams = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParamsString)
      params.delete('dateTo')
      params.set('page', String(page))
      if (!params.get('pageSize')) {
        params.set('pageSize', '20')
      }
      return params
    },
    [searchParamsString],
  )

  useEffect(() => {
    const urlRadius = normalizeRadiusParam(searchParams.get('radius'))
    if (urlRadius !== radiusInput) {
      setRadiusInput(urlRadius)
    }
    // Keep local inputs synced only when URL/search params change
    // (not on every keystroke).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsString])

  // Surface a one-shot success toast when redirected here after submitting a
  // new event draft, then strip the `submitted` flag so the toast does not
  // re-fire on back/forward navigation.
  useEffect(() => {
    const submitted = searchParams.get('submitted')
    if (submitted !== 'event') return
    toast.success('Event submitted as draft.', {
      description: 'It will appear here once it is published.',
    })
    const next = new URLSearchParams(searchParamsString)
    next.delete('submitted')
    const query = next.toString()
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setURLState = (updates: Record<string, string | null | undefined>) => {
    const next = buildSearchParams(new URLSearchParams(searchParamsString), updates)
    const nextQuery = next.toString()
    const href = nextQuery ? `${pathname}?${nextQuery}` : pathname
    router.replace(href, { scroll: false })
  }

  useEffect(() => {
    const currentRadius = normalizeRadiusParam(searchParams.get('radius'))
    if (!searchParams.get('lat') || !searchParams.get('lng')) return
    if (debouncedRadius === currentRadius) return
    setURLState({
      radius: debouncedRadius,
      page: null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRadius])

  useEffect(() => {
    const controller = new AbortController()
    const resultKey = searchParamsString
    currentResultKeyRef.current = resultKey
    const params = buildEventRequestParams(1)

    const loadResults = async () => {
      setLoadingResults(true)
      setLoadingMoreResults(false)
      setResultsError(null)
      setLoadMoreError(null)
      try {
        const response = await fetch(`/api/v1/events?${params.toString()}`, {
          method: 'GET',
          signal: controller.signal,
        })
        const data = (await response.json()) as EventSearchResponse & { error?: string }

        if (!response.ok) {
          setResultsError(data.error || 'Could not load events.')
          setResults(null)
          return
        }

        setResults(data)
      } catch (error) {
        if ((error as Error).name === 'AbortError') return
        setResultsError('Could not load events.')
        setResults(null)
      } finally {
        if (!controller.signal.aborted) {
          setLoadingResults(false)
        }
      }
    }

    void loadResults()
    return () => controller.abort()
  }, [buildEventRequestParams, resultRetrySeed, searchParamsString])

  const loadNextPage = useCallback(async () => {
    if (!results || loadingResults || loadingMoreResults || !hasMoreResults) return

    const resultKey = currentResultKeyRef.current
    const params = buildEventRequestParams(results.page + 1)
    setLoadingMoreResults(true)
    setLoadMoreError(null)

    try {
      const response = await fetch(`/api/v1/events?${params.toString()}`, {
        method: 'GET',
      })
      const data = (await response.json()) as EventSearchResponse & { error?: string }

      if (!response.ok) {
        setLoadMoreError(data.error || 'Could not load more events.')
        return
      }

      if (currentResultKeyRef.current !== resultKey) return

      setResults((current) => {
        if (!current || currentResultKeyRef.current !== resultKey) return current
        const seenIds = new Set(current.items.map((item) => item.id))
        const newItems = data.items.filter((item) => !seenIds.has(item.id))
        return {
          ...data,
          items: [...current.items, ...newItems],
        }
      })
    } catch {
      if (currentResultKeyRef.current === resultKey) {
        setLoadMoreError('Could not load more events.')
      }
    } finally {
      if (currentResultKeyRef.current === resultKey) {
        setLoadingMoreResults(false)
      }
    }
  }, [buildEventRequestParams, hasMoreResults, loadingMoreResults, loadingResults, results])

  const selectRadius = (radius: string) => {
    setRadiusInput(radius)
    if (!hasAddressLocation) return

    setURLState({
      radius,
      page: null,
    })
  }

  const renderRadiusOptions = (className = '') => (
    <div className={className}>
      <p className="px-2 text-xs font-medium text-muted-foreground">Radius</p>
      <div className="grid grid-cols-4 gap-1 px-2 pt-2">
        {LOCATION_RADIUS_OPTIONS.map((radius) => {
          const isSelected = radiusInput === radius
          return (
            <button
              key={radius}
              type="button"
              className={`rounded-sm border px-2 py-1.5 text-xs transition-colors ${
                isSelected
                  ? 'border-theme-button-primary-background bg-theme-button-primary-background text-theme-button-primary-foreground'
                  : 'border-theme-surface-border hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground'
              }`}
              aria-pressed={isSelected}
              onClick={() => selectRadius(radius)}
            >
              {radius}
            </button>
          )
        })}
      </div>
      <p className="px-2 pt-1 text-[11px] text-muted-foreground">Miles from current location or address.</p>
    </div>
  )

  useEffect(() => {
    const target = loadMoreRef.current
    if (!target || !hasMoreResults || loadingResults || loadingMoreResults || resultsError) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadNextPage()
        }
      },
      { rootMargin: '600px 0px' },
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [hasMoreResults, loadNextPage, loadingMoreResults, loadingResults, resultsError])

  useEffect(() => {
    if (locationDialogMode !== 'address' || !debouncedLocationQuery.trim()) {
      setPredictions([])
      setLoadingPredictions(false)
      return
    }

    const controller = new AbortController()
    const loadPredictions = async () => {
      setLoadingPredictions(true)
      setLocationError(null)
      try {
        const params = new URLSearchParams({ input: debouncedLocationQuery.trim() })
        const response = await fetch(`/api/google-places?${params.toString()}`, {
          method: 'GET',
          signal: controller.signal,
        })
        const data = (await response.json()) as {
          predictions?: Prediction[]
          error?: string
        }

        if (!response.ok) {
          setPredictions([])
          setLocationError(data.error || 'Could not fetch location suggestions.')
          return
        }

        setPredictions(data.predictions || [])
      } catch (error) {
        if ((error as Error).name === 'AbortError') return
        setPredictions([])
        setLocationError('Could not fetch location suggestions.')
      } finally {
        setLoadingPredictions(false)
      }
    }

    void loadPredictions()
    return () => controller.abort()
  }, [debouncedLocationQuery, locationDialogMode])

  const applyPlaceSelection = async (placeId: string) => {
    setLocationError(null)
    try {
      const response = await fetch(`/api/google-places?${new URLSearchParams({ placeId }).toString()}`, {
        method: 'GET',
      })
      const data = (await response.json()) as GooglePlaceDetails
      if (!response.ok) {
        setLocationError(data.error || 'Could not load selected location.')
        return
      }

      setLocationQuery(data.formattedAddress || '')
      setPredictions([])
      setURLState({
        address: data.formattedAddress || null,
        metroArea: null,
        lat: typeof data.lat === 'number' ? String(data.lat) : null,
        lng: typeof data.lng === 'number' ? String(data.lng) : null,
        radius: radiusInput || DEFAULT_LOCATION_RADIUS,
        page: null,
      })
      setLocationDialogMode(null)
    } catch {
      setLocationError('Could not load selected location.')
    }
  }

  const useMyLocation = () => {
    setLocationError(null)
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported on this browser.')
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationQuery('')
        setPredictions([])
        setURLState({
          address: 'Current location',
          metroArea: null,
          lat: String(position.coords.latitude),
          lng: String(position.coords.longitude),
          radius: radiusInput || DEFAULT_LOCATION_RADIUS,
          page: null,
        })
        setLocationPopoverOpen(false)
      },
      () => {
        setLocationError('Location permission was denied or unavailable.')
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 120000 },
    )
  }

  const applyMetroAreaSelection = (metroArea: MetroAreaOption) => {
    setLocationQuery('')
    setMetroAreaQuery('')
    setPredictions([])
    setURLState({
      address: null,
      metroArea: metroArea.id,
      lat: null,
      lng: null,
      radius: null,
      page: null,
    })
    setLocationDialogMode(null)
  }

  const clearLocation = () => {
    setLocationQuery('')
    setMetroAreaQuery('')
    setPredictions([])
    setURLState({
      address: null,
      metroArea: null,
      lat: null,
      lng: null,
      radius: null,
      page: null,
    })
    setLocationPopoverOpen(false)
    setLocationDialogMode(null)
  }

  const openLocationDialog = (mode: 'metro' | 'address') => {
    setLocationPopoverOpen(false)
    setLocationDialogMode(mode)
    setLocationError(null)
    setPredictions([])
    if (mode === 'metro') {
      setMetroAreaQuery('')
      return
    }
    setLocationQuery('')
  }

  const handleLocationPopoverOpenChange = (open: boolean) => {
    setLocationPopoverOpen(open)
    if (!open) {
      setLocationQuery('')
      setMetroAreaQuery('')
      setPredictions([])
      setLocationError(null)
    }
  }

  const handleLocationDialogOpenChange = (open: boolean) => {
    if (open) return
    setLocationDialogMode(null)
    setLocationQuery('')
    setMetroAreaQuery('')
    setPredictions([])
    setLocationError(null)
  }

  const filterInputTriggerClass =
    'theme-input flex h-11 min-h-11 min-w-0 items-center justify-between gap-2 px-3 text-left transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'
  const filterInputIconTriggerClass =
    'theme-input inline-flex h-11 w-11 min-h-11 min-w-11 shrink-0 items-center justify-center p-0 text-theme-button-secondary-foreground transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'

  return (
    <section className="w-full">
      <div className="sticky top-[55px] md:top-[70px] z-20 border-b border-theme-surface-border bg-theme-row-1">
        <div className="mx-auto w-full max-w-6xl px-4 py-4 md:px-10">
          <div className="flex min-w-0 flex-nowrap items-center gap-2 md:gap-3">
            <Popover open={typePopoverOpen} onOpenChange={setTypePopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`${filterInputTriggerClass} flex-1 md:w-[250px] md:flex-none text-xs`}
                >
                  <span className="min-w-0 flex-1 truncate">{selectedFilterLabel}</span>
                  <ChevronDown className="size-4 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[min(90vw,420px)] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search categories..." />
                  <CommandList>
                    <CommandEmpty>No matching categories found.</CommandEmpty>
                    <CommandGroup heading="Status">
                      {EVENT_STATUS_OPTIONS.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={`Status ${option.label}`}
                          onSelect={() => {
                            setURLState({
                              eventStatus: option.value,
                              'event-status': null,
                              category: null,
                              'talent-category': null,
                              talentCategory: null,
                              'talent-child-category': null,
                              talentChildCategory: null,
                              page: null,
                            })
                            setTypePopoverOpen(false)
                          }}
                        >
                          {option.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandGroup heading="Browse">
                      <CommandItem
                        onSelect={() => {
                          setURLState({
                            eventStatus: null,
                            'event-status': null,
                            category: null,
                            'talent-category': null,
                            talentCategory: null,
                            'talent-child-category': null,
                            talentChildCategory: null,
                            page: null,
                          })
                          setTypePopoverOpen(false)
                        }}
                      >
                        All Categories
                      </CommandItem>
                    </CommandGroup>
                    {eventCategories.map((category) => (
                      <CommandGroup key={category.id} heading={category.pluralName}>
                        {category.children.map((child) => (
                          <CommandItem
                            key={child.id}
                            value={`${category.pluralName} ${child.name}`}
                            onSelect={() => {
                              setURLState({
                                eventStatus: null,
                                'event-status': null,
                                category: child.slug,
                                'talent-category': null,
                                talentCategory: null,
                                'talent-child-category': null,
                                talentChildCategory: null,
                                page: null,
                              })
                              setTypePopoverOpen(false)
                            }}
                          >
                            {child.name}
                          </CommandItem>
                        ))}
                        <CommandItem
                          value={`${category.pluralName} view all`}
                          onSelect={() => {
                            setURLState({
                              eventStatus: null,
                              'event-status': null,
                              category: category.slug,
                              'talent-category': null,
                              talentCategory: null,
                              'talent-child-category': null,
                              talentChildCategory: null,
                              page: null,
                            })
                            setTypePopoverOpen(false)
                          }}
                        >
                          View all {category.pluralName}
                        </CommandItem>
                      </CommandGroup>
                    ))}
                    {talentCategories.map((category) => (
                      <CommandGroup key={category.id} heading={category.pluralName}>
                        {category.children.map((child) => (
                          <CommandItem
                            key={child.id}
                            value={`${category.pluralName} ${child.name}`}
                            onSelect={() => {
                              setURLState({
                                eventStatus: null,
                                'event-status': null,
                                category: null,
                                'talent-category': null,
                                talentCategory: null,
                                'talent-child-category': child.slug,
                                talentChildCategory: null,
                                page: null,
                              })
                              setTypePopoverOpen(false)
                            }}
                          >
                            {child.name}
                          </CommandItem>
                        ))}
                        <CommandItem
                          value={`${category.pluralName} view all`}
                          onSelect={() => {
                            setURLState({
                              eventStatus: null,
                              'event-status': null,
                              category: null,
                              'talent-category': category.slug,
                              talentCategory: null,
                              'talent-child-category': null,
                              talentChildCategory: null,
                              page: null,
                            })
                            setTypePopoverOpen(false)
                          }}
                        >
                          View all {category.pluralName}
                        </CommandItem>
                      </CommandGroup>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            <EventsPageSearchButton
              triggerClassName={`${filterInputIconTriggerClass} shrink-0`}
              buildEventHref={(id) => `${basePath}/${id}`}
            />

            <Popover open={locationPopoverOpen} onOpenChange={handleLocationPopoverOpenChange}>
              <PopoverTrigger asChild>
                {hasLocation ? (
                  <button
                    type="button"
                    className={`${filterInputTriggerClass} ml-auto min-w-0 flex-1 md:w-[250px] md:flex-none text-xs`}
                  >
                    <MapPin className="size-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">
                      {selectedLocationLabel || 'Location set'}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`${filterInputIconTriggerClass} ml-auto shrink-0`}
                    aria-label="Set location"
                  >
                    <MapPin className="size-4 stroke-[2.25]" />
                  </button>
                )}
              </PopoverTrigger>
              <PopoverContent className="w-[min(90vw,260px)] p-1" align="start">
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    className="block w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground"
                    onClick={useMyLocation}
                  >
                    Use My Location
                  </button>
                  <button
                    type="button"
                    className="block w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground"
                    onClick={() => openLocationDialog('metro')}
                  >
                    Search by Metro Area
                  </button>
                  <button
                    type="button"
                    className="block w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground"
                    onClick={() => openLocationDialog('address')}
                  >
                    Search near an Address
                  </button>
                  {hasLocation && (
                    <button
                      type="button"
                      className="block w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground"
                      onClick={clearLocation}
                    >
                      Clear Location
                    </button>
                  )}
                  {!selectedMetroArea && renderRadiusOptions('border-t border-theme-surface-border py-2')}
                </div>
              </PopoverContent>
            </Popover>

            <CommandDialog
              open={locationDialogMode === 'metro'}
              onOpenChange={handleLocationDialogOpenChange}
              title="Search by Metro Area"
              description="Choose a metro area to filter events."
            >
              <CommandInput
                value={metroAreaQuery}
                onValueChange={(value) => {
                  setMetroAreaQuery(value)
                  setLocationQuery('')
                  setPredictions([])
                }}
                placeholder="Search metro areas..."
              />
              <CommandList>
                <CommandEmpty>No metro areas found.</CommandEmpty>
                <CommandGroup heading="Metro Areas">
                  {filteredMetroAreas.slice(0, 25).map((metroArea) => (
                    <CommandItem
                      key={metroArea.id}
                      value={metroArea.label}
                      onSelect={() => applyMetroAreaSelection(metroArea)}
                    >
                      {metroArea.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </CommandDialog>

            <CommandDialog
              open={locationDialogMode === 'address'}
              onOpenChange={handleLocationDialogOpenChange}
              title="Search near an Address"
              description="Choose an address to search for nearby events."
            >
              <CommandInput
                value={locationQuery}
                onValueChange={(value) => {
                  setLocationQuery(value)
                  setMetroAreaQuery('')
                }}
                placeholder="Search for an address..."
              />
              {renderRadiusOptions('border-b border-theme-surface-border py-3')}
              <CommandList>
                {loadingPredictions ? (
                  <CommandGroup>
                    <CommandItem disabled>
                      <Loader2 className="animate-spin" />
                      Searching addresses...
                    </CommandItem>
                  </CommandGroup>
                ) : locationError ? (
                  <CommandGroup>
                    <CommandItem disabled>{locationError}</CommandItem>
                  </CommandGroup>
                ) : locationQuery.trim().length === 0 ? (
                  <CommandEmpty>Type to search for an address.</CommandEmpty>
                ) : predictions.length > 0 ? (
                  <CommandGroup heading="Addresses">
                    {predictions.map((prediction) => (
                      <CommandItem
                        key={prediction.placeId}
                        value={prediction.description}
                        onSelect={() => void applyPlaceSelection(prediction.placeId)}
                      >
                        {prediction.description}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : (
                  <CommandEmpty>No addresses found.</CommandEmpty>
                )}
              </CommandList>
            </CommandDialog>

            <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`${filterInputIconTriggerClass} shrink-0`}
                  aria-label={selectedDate ? `Filtering from ${formatDateLabel(selectedDate)}` : 'Filter by date'}
                >
                  <CalendarDays className="size-4 stroke-[2.25]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  defaultMonth={selectedDate}
                  onSelect={(date) => {
                    setURLState({
                      dateFrom: date ? formatDateParam(date) : null,
                      dateTo: null,
                      page: null,
                    })
                    if (date) {
                      setDatePopoverOpen(false)
                    }
                  }}
                  disabled={(date) =>
                    date < new Date(new Date().getFullYear() - 5, 0, 1) ||
                    date > new Date(new Date().getFullYear() + 5, 11, 31)
                  }
                  initialFocus
                />
                <div className="border-t border-theme-surface-border p-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setURLState({
                        dateFrom: null,
                        dateTo: null,
                        page: null,
                      })
                      setDatePopoverOpen(false)
                    }}
                    disabled={!selectedDate && !searchParams.get('dateTo')}
                  >
                    Clear Date
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {showAdvancedEventItems ? (
              <Link
                href="/app/events/submit"
                aria-label="Submit a new event"
                className={`${filterInputIconTriggerClass} shrink-0`}
              >
                <Plus className="size-4 stroke-[2.25]" />
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-10 md:py-8">
        {loadingResults ? (
          <EventResultSkeleton />
        ) : resultsError ? (
          <Card>
            <CardContent className="flex flex-col items-start gap-3 p-4 md:p-6">
              <p className="text-red-500">{resultsError}</p>
              <Button type="button" onClick={() => setResultRetrySeed((value) => value + 1)}>
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : results && results.items.length > 0 ? (
          <Card>
            <CardContent className="p-0">
              <ul className="divide-y theme-card-divider">
                {results.items.map((item) => (
                  <EventCardListItem
                    key={item.id}
                    event={{
                      name: item.name,
                      date: item.date,
                      date_tz: item.dateTz ?? null,
                      end_date: item.endDate ?? null,
                      end_date_tz: item.endDateTz ?? null,
                      event_status: item.event_status ?? null,
                      volunteers_needed: item.volunteersNeeded ?? null,
                    }}
                    venueName={item.venue?.name ?? null}
                    venueCityState={item.venue ? { city: item.venue.city, state: item.venue.state } : null}
                    artistNamesFromSearch={item.artists.map((a) => a.name)}
                    teamLeaderNames={item.teamLeaderNames ?? undefined}
                    subtitle={item.tour?.name ? `Tour: ${item.tour.name}` : undefined}
                    href={`${basePath}/${item.id}`}
                    showAdvancedFeatures={showAdvancedEventItems}
                    confirmedVolunteerCount={item.confirmedVolunteerCount ?? null}
                    volunteersNeeded={item.volunteersNeeded ?? null}
                    volunteerStatus={item.volunteerStatus ?? null}
                  />
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : results ? (
          <Card>
            <CardContent className="p-4 md:p-6">
              <p>No events match your current filters.</p>
            </CardContent>
          </Card>
        ) : null}

        {results && results.items.length > 0 && (
          <div className="mt-4 flex flex-col items-center gap-3">
            {loadMoreError ? (
              <>
                <p className="text-sm text-red-500">{loadMoreError}</p>
                <Button type="button" variant="secondary" onClick={() => void loadNextPage()}>
                  Retry
                </Button>
              </>
            ) : hasMoreResults ? (
              <>
                <div ref={loadMoreRef} className="h-1 w-full" aria-hidden="true" />
                {loadingMoreResults ? (
                  <div className="theme-card-content flex items-center gap-2 text-sm">
                    <Loader2 className="size-4 animate-spin" />
                    Loading more events...
                  </div>
                ) : (
                  <Button type="button" variant="secondary" onClick={() => void loadNextPage()}>
                    Load More Events
                  </Button>
                )}
              </>
            ) : (
              <p className="theme-card-content text-sm">All matching events loaded.</p>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
